import {
  Box,
  Button,
  ComboboxItem,
  Grid,
  LoadingOverlay,
  Menu,
  Select,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import {
  IconArrowBack,
  IconCheck,
  IconFilePlus,
  IconPackages,
  IconReceipt2,
  IconSearch,
  IconUserCircle,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { repositoryMdm, repositoryPos } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import {
  getValueById,
  handleKeyDownPhone,
} from "../../../_base/helper/FunctionHelper";
import { MessageResponse } from "../../../model/MessageResponse";
import { TblDmWarrantyReceiveTicket } from "../../../model/TblDmWarrantyReceiveTicket";
import { TblWarrantyServiceRequestForm } from "../../../model/TblWarrantyServiceRequestForm";
import {
  getItemPositionSelect,
  getTblDmSaleChanelSelect,
} from "../../../service/getSelectApi";
import ProductWarrantyList from "./ProductWarrantyList";
import { PageHeader, SectionCard } from "./ReceiveTicketUI";
import ReceiveWarrantyList from "./ReceiveWarrantyList";

const TYPE_PRODUCT_HACOM = "HANG_HACOM";

// Chỉ tiếp nhận các sản phẩm được duyệt "nhập kho" trên phiếu tiếp nhận yêu cầu
const STATUS_DETAIL_RECEIVE = "NHAP_KHO";

type ReceiptFormValues = TblDmWarrantyReceiveTicket & {
  customerGroup?: string | null;
};

type InventoryOption = ComboboxItem & { inventoryId: number };

// Danh mục dùng để tra cứu tên hiển thị theo id (getValueById)
type LookupItem = { id: any; name: string };

const initialFormValues: ReceiptFormValues = {
  id: "0",
  empId: null,
  requestId: null,
  errorNumber: null,
  dateReceive: new Date().toISOString(),
  inventoryCode: null,
  note: null,
  idItemPosition: null,
  branchId: null,
  idCustomerGroup: null,
  inventoryId: null,
  tblWarrantyReceiveTicketDetailCommands: null,
};

const CreateReceiptForm = ({ onClose, onSuccess }: CreateReceiptFormProps) => {
  const form = useForm<ReceiptFormValues>({
    mode: "uncontrolled",
    validateInputOnChange: true,
    initialValues: { ...initialFormValues },
    transformValues: (values) => ({
      ...values,
      idItemPosition: Number(values.idItemPosition),
    }),
    validate: {
      idItemPosition: (value) => {
        if (!value) {
          return "Vui lòng nhập vị trí kho !";
        }
      },
    },
  });

  const [dataRequestForm, setDataRequestForm] = useState<any>();
  const [dataRequestFormSelected, setDataRequestFormSelected] = useState<any>();
  const [dataEmployeeDetail, setDataEmployeeDetail] = useState<any>();
  const [dataTblSaleChannel, setDataTblSaleChannel] = useState<ComboboxItem[]>(
    [],
  );
  const [dataInventorySelect, setDataInventorySelect] = useState<
    InventoryOption[]
  >([]);
  const [dataItemPositionSelect, setDataItemPositionSelect] = useState<
    ComboboxItem[]
  >([]);
  const [dataDepartment, setDataDepartment] = useState<LookupItem[]>([]);
  const [dataRole, setDataRole] = useState<LookupItem[]>([]);
  const [phoneNumber, setPhoneNumber] = useDebouncedState("", 300);
  const [editAndSave, setEditAndSave] = useState(false);
  const [isEdit, setIsEdit] = useState({ note: false });
  const [visible, { close, open }] = useDisclosure(false);

  const { inventoryCode } = form.getValues();
  const warrantyReceiveTicketDetail =
    form.getValues().tblWarrantyReceiveTicketDetailCommands ?? [];

  // Danh sách sản phẩm phải tiếp nhận, cũng chính là thứ tự dòng hiển thị trên bảng
  const requestFormDetails = useMemo(
    () =>
      (
        dataRequestFormSelected?.tblWarrantyServiceRequestFormDetailModels ?? []
      ).filter((item: any) => item?.attribute10 === STATUS_DETAIL_RECEIVE),
    [dataRequestFormSelected],
  );

  const getDataRequestForm = async () => {
    try {
      const res = await repositoryMdm.get<
        MessageResponse<TblWarrantyServiceRequestForm[]>
      >(
        `api/v1/TblWarrantyServiceRequestForm/get-list-v2?isReceive=false&RequestStatus=DA_XU_LY&KeySearch=${phoneNumber}&Take=100`,
      );

      if (res?.success) {
        setDataRequestForm(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Lấy số phiếu nhận hàng lỗi được sinh tự động theo phiếu tiếp nhận yêu cầu
  const getErrorNumber = async (codeRequestForm: string) => {
    open();
    try {
      const res = await repositoryMdm.get<
        MessageResponse<TblDmWarrantyReceiveTicket>
      >(
        `/api/v1/TblWarrantyReceiveTicket/create?prefix=PNHL&code=${codeRequestForm}`,
      );

      if (!res?.success) {
        onClose("");
        return;
      }

      form.setValues((prev) => ({
        ...prev,
        errorNumber: res.data?.errorNumber,
      }));
    } finally {
      close();
    }
  };

  const getSaleChannelSelect = async () => {
    try {
      const res = await getTblDmSaleChanelSelect();

      setDataTblSaleChannel(
        res
          ?.filter(
            (item) =>
              item.value != null && item.text != null && item.value !== "-1",
          )
          ?.map((item) => ({ value: item.value, label: item.text })),
      );
    } catch (error) {
      console.error("Error in dataTblSaleChannel: ", error);
    }
  };

  const getInventorySelect = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      `/api/v1/TblDmInventory/get-select-by-branch?branchId=${dataRequestFormSelected?.branchId}`,
    );

    if (!res?.success) return;

    // Hàng HACOM lấy kho bảo hành (BH), hàng ngoài lấy kho bảo khách (BK)
    const inventoryPrefixes =
      dataRequestFormSelected?.attribute6?.toString() === TYPE_PRODUCT_HACOM
        ? ["BH", "DV"]
        : ["BK", "DV"];

    setDataInventorySelect(
      res.data
        ?.filter(
          (item: any) =>
            item.inventoryId != null &&
            item.inventoryName != null &&
            inventoryPrefixes.some((prefix) =>
              item.inventoryCode?.includes(prefix),
            ),
        )
        ?.map((item: any) => ({
          value: item.inventoryCode?.toString(),
          label: `${item.inventoryName} - ${item.inventoryCode}`,
          inventoryId: item.inventoryId,
        })),
    );
  };

  const getDataItemPosition = async () => {
    const res = await getItemPositionSelect(inventoryCode);

    if (!res) return;

    const positions = res.filter(
      (item: any) => item.id != null && item.positionName != null,
    );
    const positionsFirst = positions.filter((item: any) =>
      item.positionCode?.includes("_01"),
    );

    // Ưu tiên vị trí "_01" lên đầu, nếu không có thì chỉ lấy vị trí sửa chữa "SC"
    const finalData =
      positionsFirst.length > 0
        ? [
            ...positionsFirst,
            ...positions.filter(
              (item: any) => !item.positionCode?.includes("_01"),
            ),
          ]
        : positions.filter((item: any) => item.positionCode?.includes("SC"));

    setDataItemPositionSelect(
      finalData.map((item: any) => ({
        value: item.id?.toString(),
        label: `${item.positionCode} - ${item.positionName}`,
      })),
    );
  };

  const getDataDepartment = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblDepartment/get-all",
    );

    if (res?.success) {
      setDataDepartment(
        res.data
          ?.filter((item: any) => item.name != null)
          ?.map((item: any) => ({ id: item.departmentId, name: item.name })),
      );
    }
  };

  const getDataRole = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblRole/get-all",
    );

    if (res?.success) {
      setDataRole(
        res.data
          ?.filter((item: any) => item.name != null)
          ?.map((item: any) => ({ id: item.id, name: item.name })),
      );
    }
  };

  const getEmployeeDetail = async () => {
    if (!dataRequestFormSelected?.empId) {
      setDataEmployeeDetail(undefined);
      return;
    }

    const res = await repositoryPos.get<MessageResponse<any>>(
      `/api/v1/TblDmEmployee/detail?id=${dataRequestFormSelected?.empId}`,
    );

    if (res?.success) {
      setDataEmployeeDetail(res.data);
    }
  };

  // Mỗi dòng người dùng sửa được ghép lại thành 1 sản phẩm tiếp nhận, các thông tin
  // còn lại lấy nguyên theo sản phẩm tương ứng của phiếu tiếp nhận yêu cầu
  const handleChangeValue = (key: string, value: any, index: number) => {
    const requestFormDetail = requestFormDetails[index];

    form.setValues((prevData) => {
      const prevDetails: any[] = Array.isArray(
        prevData.tblWarrantyReceiveTicketDetailCommands,
      )
        ? prevData.tblWarrantyReceiveTicketDetailCommands
        : [];

      if (prevDetails[index]?.[key] === value) {
        return prevData;
      }

      const updatedDetails = [...prevDetails];
      updatedDetails[index] = {
        ...updatedDetails[index],
        [key]: value,
        id: Number(index),
        itemId: requestFormDetail?.itemId,
        serial: requestFormDetail?.serial?.trim(),
        warrantyTerm: requestFormDetail?.warrantyTerm,
        orderCode: requestFormDetail?.orderCode,
        buyingDate: requestFormDetail?.buyingDate,
        amount: 1,
        serviceType:
          value && key === "serviceType"
            ? value
            : requestFormDetail?.serviceType,
        scheduleTime: requestFormDetail?.entryDate,
        daysReturn: requestFormDetail?.attribute5,
        requestFormDetailId: requestFormDetail?.id,
        attribute1: dataRequestFormSelected?.attribute6,
      };

      return {
        ...prevData,
        tblWarrantyReceiveTicketDetailCommands: updatedDetails,
      };
    });
  };

  const handleCreateReceiptForm = async (dataSubmit: ReceiptFormValues) => {
    const details = dataSubmit?.tblWarrantyReceiveTicketDetailCommands ?? [];

    // Phải nhập đủ loại dịch vụ cho tất cả sản phẩm cần tiếp nhận
    if (
      details.length === 0 ||
      details.length !== requestFormDetails.length ||
      details.some((item) => !item.serviceType)
    ) {
      NotificationExtension.Fails(
        "Vui lòng nhập đầy đủ loại dịch vụ của các sản phẩm !",
      );
      return;
    }

    open();
    try {
      const res = await repositoryMdm.post<
        MessageResponse<TblDmWarrantyReceiveTicket>
      >("/api/v1/TblWarrantyReceiveTicket/create", dataSubmit);

      if (res?.success) {
        NotificationExtension.Success("Tiếp nhận hàng lỗi thành công !");
        setTimeout(() => {
          onSuccess((prev: any) => !prev);
          onClose("");
        }, 1500);
      }
    } catch (error) {
      console.error(error);
    } finally {
      close();
    }
  };

  const resetEditState = () => {
    setIsEdit({ note: false });
    setEditAndSave(false);
  };

  useEffect(() => {
    getSaleChannelSelect();
    getDataDepartment();
    getDataRole();
  }, []);

  useEffect(() => {
    getDataRequestForm();
  }, [phoneNumber]);

  useEffect(() => {
    if (!dataRequestFormSelected) return;

    getErrorNumber(dataRequestFormSelected?.requestNum);
    form.setValues((prev) => ({
      ...prev,
      requestId: dataRequestFormSelected?.id,
      empId: dataRequestFormSelected?.empId,
      branchId: dataRequestFormSelected?.branchId,
      customerGroup: dataRequestFormSelected?.customerGroup,
      note: dataRequestFormSelected?.content || "",
    }));
  }, [dataRequestFormSelected]);

  useEffect(() => {
    getEmployeeDetail();
  }, [dataRequestFormSelected?.empId]);

  useEffect(() => {
    if (dataRequestFormSelected?.branchId) {
      getInventorySelect();
    } else {
      setDataInventorySelect([]);
    }
  }, [dataRequestFormSelected?.branchId]);

  useEffect(() => {
    if (inventoryCode) {
      getDataItemPosition();
    } else {
      setDataItemPositionSelect([]);
    }
  }, [inventoryCode]);

  useEffect(() => {
    if (dataItemPositionSelect.length > 0) {
      form.setFieldValue(
        "idItemPosition",
        Number(dataItemPositionSelect[0]?.value),
      );
    }
  }, [dataItemPositionSelect]);

  return (
    <Box
      component="form"
      mx="auto"
      p={"0px 10px"}
      onSubmit={form.onSubmit(handleCreateReceiptForm)}
      onMouseDown={resetEditState}
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <PageHeader
        icon={<IconFilePlus size={19} stroke={1.6} />}
        title="Lập phiếu nhận hàng lỗi"
        subtitle={
          form.getValues().errorNumber
            ? `Số phiếu: ${form.getValues().errorNumber}`
            : "Chọn phiếu tiếp nhận yêu cầu để bắt đầu tiếp nhận sản phẩm"
        }
        actions={
          <>
            <Button
              type="button"
              color="red"
              variant="light"
              radius="md"
              loading={visible}
              onClick={() => onClose("")}
              leftSection={!visible ? <IconArrowBack size={18} /> : undefined}
            >
              Thoát
            </Button>
            <Button
              type="submit"
              color={"teal"}
              variant="filled"
              radius="md"
              loading={visible}
              leftSection={!visible ? <IconCheck size={18} /> : undefined}
            >
              Lưu
            </Button>
          </>
        }
      />
      <Grid gutter={"xs"}>
        <Grid.Col span={{ base: 12, md: 6, lg: 4.5 }}>
          <SectionCard
            icon={<IconUserCircle size={15} stroke={1.7} />}
            title="Thông tin khách hàng hẹn gặp"
            color="blue"
          >
            <Grid>
              <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
                <Menu
                  trigger="hover"
                  openDelay={100}
                  closeDelay={400}
                  width={1000}
                >
                  <Menu.Target>
                    <TextInput
                      label="Tìm kiếm phiếu tiếp nhận yêu cầu"
                      placeholder="Nhập từ mã phiếu, số điện thoại, mã khách hàng"
                      defaultValue={phoneNumber ?? ""}
                      leftSection={<IconSearch size={16} stroke={1.7} />}
                      onKeyDown={handleKeyDownPhone}
                      onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                    />
                  </Menu.Target>
                  <Menu.Dropdown>
                    <ReceiveWarrantyList
                      phoneNumber={phoneNumber}
                      dataRequestForm={dataRequestForm}
                      setDataRequestFormSelected={setDataRequestFormSelected}
                    />
                  </Menu.Dropdown>
                </Menu>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
                <Select
                  label="Kênh mua hàng"
                  placeholder="Kênh mua hàng"
                  data={dataTblSaleChannel}
                  value={dataRequestFormSelected?.channel?.toString() ?? null}
                  rightSection={" "}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
                <TextInput
                  label="Tên khách hàng"
                  placeholder="Tên khách hàng"
                  value={dataRequestFormSelected?.custMeeting ?? ""}
                  readOnly
                  variant="filled"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
                <TextInput
                  label="Nhóm khách hàng"
                  placeholder="Nhóm khách hàng"
                  value={dataRequestFormSelected?.customerGroupName ?? ""}
                  readOnly
                  variant="filled"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
                <TextInput
                  label="Số điện thoại"
                  placeholder="Số điện thoại"
                  value={dataRequestFormSelected?.custPhone ?? ""}
                  readOnly
                  variant="filled"
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ"
                  placeholder="Địa chỉ"
                  value={[
                    dataRequestFormSelected?.additionalAddress,
                    dataRequestFormSelected?.meetingAddress,
                  ]
                    .filter((item) => item && String(item).length > 0)
                    .join(" - ")}
                  readOnly
                  variant="filled"
                />
              </Grid.Col>
            </Grid>
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 7.5 }}>
          <SectionCard
            icon={<IconReceipt2 size={15} stroke={1.7} />}
            title="Thông tin phiếu tiếp nhận hàng lỗi"
            color="cyan"
          >
            <Grid>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Số phiếu tiếp nhận hàng lỗi"
                  placeholder="Số phiếu tiếp nhận hàng lỗi"
                  value={form.getValues().errorNumber ?? ""}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Mã phiếu tiếp nhận yêu cầu"
                  placeholder="Mã phiếu tiếp nhận yêu cầu"
                  value={dataRequestFormSelected?.requestNum ?? ""}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Số phiếu tiếp nhận yêu cầu"
                  placeholder="Số phiếu tiếp nhận yêu cầu"
                  value={dataRequestFormSelected?.attribute10 ?? ""}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <DateTimePicker
                  label="Ngày tạo phiếu"
                  valueFormat="DD-MM-YYYY hh:mm A"
                  value={new Date()}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Hình thức xử lý"
                  placeholder="Hình thức xử lý"
                  value={dataRequestFormSelected?.processingMethodName ?? ""}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Nhân viên tiếp nhận"
                  placeholder="Nhập tên nhân viên tiếp nhận"
                  value={dataRequestFormSelected?.empnameincharge ?? ""}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Chức danh"
                  placeholder="Chức danh của nhân viên tiếp nhận"
                  value={getValueById(
                    dataEmployeeDetail?.roleId,
                    dataRole,
                    "name",
                  )}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                <TextInput
                  label="Bộ phận/Phòng ban"
                  placeholder="Bộ phận/Phòng ban của nhân viên tiếp nhận"
                  value={getValueById(
                    dataEmployeeDetail?.departmentId,
                    dataDepartment,
                    "name",
                  )}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                <Select
                  label="Kho"
                  placeholder="Nhập mã hoặc tên kho"
                  data={dataInventorySelect}
                  value={form.getValues().inventoryCode?.toString() ?? null}
                  onChange={(value) =>
                    form.setValues((prev) => ({
                      ...prev,
                      inventoryCode: value,
                      inventoryId: Number(
                        getValueById(
                          value ?? "",
                          dataInventorySelect,
                          "inventoryId",
                        ),
                      ),
                    }))
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                <Select
                  label="Vị trí kho"
                  placeholder="Nhập vị trí kho"
                  data={dataItemPositionSelect}
                  value={form.getValues().idItemPosition?.toString() ?? null}
                  error={form.errors.idItemPosition}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                <TextInput
                  label="Ghi chú"
                  placeholder="Nhập ghi chú"
                  key={form.key("note")}
                  {...form.getInputProps("note")}
                />
              </Grid.Col>
            </Grid>
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={12}>
          <SectionCard
            icon={<IconPackages size={15} stroke={1.7} />}
            title="Thông tin sản phẩm"
            color="indigo"
          >
            <ProductWarrantyList
              dataRequestFormSelectedDetail={requestFormDetails}
              warrantyReceiveTicketDetail={warrantyReceiveTicketDetail}
              editAndSave={editAndSave}
              setEditAndSave={setEditAndSave}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
              handleChangeValue={handleChangeValue}
              typeProduct={dataRequestFormSelected?.attribute6}
              actionView={"CREATE"}
            />
          </SectionCard>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

type CreateReceiptFormProps = {
  onClose?: any;
  onSuccess?: any;
};

export default CreateReceiptForm;
